import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, FileSpreadsheet, Upload } from "lucide-react";
import { apiRequest } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const TARGETS = [
  { value: "workers", label: "Lavoratori", columns: "full_name (obbligatorio), fiscal_code (obbligatorio), job_title (obbligatorio), birth_date, employment_type, vulnerable, pregnant, minor, disabled, night, notes" },
  { value: "job_tasks", label: "Mansioni", columns: "name (obbligatorio), description, site_id, department" },
  { value: "chemical_substances", label: "Sostanze chimiche", columns: "name (obbligatorio), supplier, cas_number, classification, hazard_categories (lista separata da virgole), is_cmr, annual_consumption, exposure_assessment, sds_link, notes" },
];

export default function CsvImport() {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [target, setTarget] = useState("workers");
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const canImport = ["SUPER_ADMIN", "CONSULENTE", "AZIENDA_ADMIN", "RSPP"].includes(user.role);

  useEffect(() => { apiRequest("/companies").then(setCompanies).catch((err) => setError(err.message)); }, []);

  const runRequest = async (mode) => {
    setError("");
    if (!companyId) { setError("Seleziona un'azienda di destinazione"); return; }
    if (!file) { setError("Seleziona un file CSV"); return; }
    setBusy(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const path = `/companies/${companyId}/import/${target}${mode === "preview" ? "/preview" : ""}`;
      const res = await apiRequest(path, { method: "POST", body: data });
      if (mode === "preview") { setPreview(res); setResult(null); }
      else { setResult(res); setPreview(null); }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const submit = (e) => { e.preventDefault(); runRequest("import"); };
  const previewDry = (e) => { e.preventDefault(); runRequest("preview"); };

  const targetMeta = TARGETS.find((t) => t.value === target);

  return (
    <div className="space-y-6" data-testid="csv-import-page">
      <section className="rounded-lg border border-slate-200 bg-white p-6">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-900">Import dati massivi</p>
        <h2 className="mt-2 font-heading text-4xl font-bold text-slate-950">Import CSV</h2>
        <p className="mt-2 text-slate-600">Importa rapidamente lavoratori, mansioni o sostanze chimiche da un file CSV (max 2 MB, separatori `,` `;` `\t` `|` auto-rilevati, encoding UTF-8 o Latin-1).</p>
      </section>

      {error && <Alert variant="destructive" data-testid="csv-error"><AlertTriangle className="h-4 w-4" /><AlertTitle>Errore</AlertTitle><AlertDescription>{error}</AlertDescription></Alert>}

      {!canImport && <Alert className="border-amber-200 bg-amber-50 text-amber-900"><AlertTriangle className="h-4 w-4" /><AlertTitle>Permessi insufficienti</AlertTitle><AlertDescription>Il tuo ruolo non consente di importare dati. Richiedi accesso a un amministratore.</AlertDescription></Alert>}

      {canImport && (
        <Card className="shadow-none" data-testid="csv-form-card">
          <CardHeader><CardTitle className="font-heading flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-blue-900" /> Carica CSV</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={submit} className="grid gap-3 md:grid-cols-2" data-testid="csv-form">
              <div className="space-y-2"><Label>Azienda destinazione</Label><select className="h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" value={companyId} onChange={(e) => setCompanyId(e.target.value)} required data-testid="csv-company-select"><option value="">— Seleziona azienda —</option>{companies.map((c) => <option key={c.id} value={c.id}>{c.business_name}</option>)}</select></div>
              <div className="space-y-2"><Label>Tipo dato</Label><select className="h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" value={target} onChange={(e) => setTarget(e.target.value)} data-testid="csv-target-select">{TARGETS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}</select></div>
              <div className="space-y-2 md:col-span-2 rounded-md bg-slate-50 p-3 text-xs text-slate-600" data-testid="csv-columns-help">
                <strong>Colonne attese:</strong> {targetMeta?.columns}
              </div>
              <div className="space-y-2 md:col-span-2"><Label>File CSV</Label><Input type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] || null)} required data-testid="csv-file-input" /></div>
              <div className="flex flex-col gap-2 md:col-span-2 md:flex-row">
                <Button type="button" variant="outline" onClick={previewDry} disabled={busy} className="flex-1" data-testid="csv-preview-button"><Upload className="h-4 w-4" /> {busy ? "Analisi..." : "Anteprima (dry-run)"}</Button>
                <Button type="submit" className="flex-1 bg-blue-900 hover:bg-blue-800" disabled={busy} data-testid="csv-submit-button"><Upload className="h-4 w-4" /> {busy ? "Importazione..." : "Importa CSV"}</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {preview && (
        <Card className="shadow-none border-amber-200" data-testid="csv-preview-card">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-amber-700" /> Anteprima dry-run · nessun dato salvato</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="rounded-md bg-blue-50 p-3"><p className="text-xs text-blue-700">Righe lette</p><p className="font-heading text-2xl font-bold text-blue-900" data-testid="csv-preview-total">{preview.total_rows}</p></div>
              <div className="rounded-md bg-emerald-50 p-3"><p className="text-xs text-emerald-700">Pronte all&apos;import</p><p className="font-heading text-2xl font-bold text-emerald-900" data-testid="csv-preview-valid">{preview.inserted}</p></div>
              <div className="rounded-md bg-red-50 p-3"><p className="text-xs text-red-700">Da correggere</p><p className="font-heading text-2xl font-bold text-red-900" data-testid="csv-preview-skipped">{preview.skipped}</p></div>
            </div>
            <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-900" data-testid="csv-preview-hint">Anteprima senza scrittura. Le prime 5 righe valide sono mostrate sotto. Premi &quot;Importa CSV&quot; per scrivere realmente i dati.</p>
            {preview.errors.map((row) => (
              <div key={`${row.row_index}-${row.errors.length === 0 ? "ok" : "err"}`} className={`rounded-md border p-3 text-sm ${row.errors.length === 0 ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`} data-testid={`csv-preview-row-${row.row_index}`}>
                <p className={`font-semibold ${row.errors.length === 0 ? "text-emerald-900" : "text-red-900"}`}>Riga {row.row_index} {row.errors.length === 0 ? "✓ valida" : "✗ errori"}</p>
                {row.errors.length > 0 && <ul className="mt-1 list-inside list-disc text-red-800">{row.errors.map((e, idx) => <li key={idx}>{e}</li>)}</ul>}
                <p className="mt-1 text-xs text-slate-700">{JSON.stringify(row.payload)}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {result && (
        <Card className="shadow-none" data-testid="csv-result-card">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-700" /> Risultato importazione · {result.target}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="rounded-md bg-blue-50 p-3"><p className="text-xs text-blue-700">Righe lette</p><p className="font-heading text-2xl font-bold text-blue-900" data-testid="csv-result-total">{result.total_rows}</p></div>
              <div className="rounded-md bg-emerald-50 p-3"><p className="text-xs text-emerald-700">Inserite</p><p className="font-heading text-2xl font-bold text-emerald-900" data-testid="csv-result-inserted">{result.inserted}</p></div>
              <div className="rounded-md bg-red-50 p-3"><p className="text-xs text-red-700">Scartate</p><p className="font-heading text-2xl font-bold text-red-900" data-testid="csv-result-skipped">{result.skipped}</p></div>
            </div>
            {result.errors.length > 0 && (
              <div className="space-y-2" data-testid="csv-errors-list">
                <p className="text-sm font-semibold text-slate-700">Righe con errore ({result.errors.length}):</p>
                {result.errors.map((row) => (
                  <div key={row.row_index} className="rounded-md border border-red-200 bg-red-50 p-3 text-sm" data-testid={`csv-error-row-${row.row_index}`}>
                    <p className="font-semibold text-red-900">Riga {row.row_index}</p>
                    <ul className="mt-1 list-inside list-disc text-red-800">{row.errors.map((e, idx) => <li key={idx}>{e}</li>)}</ul>
                    <p className="mt-1 text-xs text-red-700"><Badge className="bg-red-100 text-red-800">Dati: {JSON.stringify(row.payload)}</Badge></p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
